import { memo } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';

import { SidebarProvider } from '@doger/ui/components/ui/sidebar';
import {
  ForgotPasswordPage,
  JWTGuard,
  ResetPasswordPage,
  useJWT,
} from '@doger/core/modules/auth';

import { AppSidebar } from '../components/sidebar';
import {
  ApplyForAnWhitelistModal,
  AuthModal,
  AuthProvider,
  EmailConfirmationModal,
  UserProfileDetailsModal,
} from '../modules/auth';
import {
  AppCookieBanner,
  LogAmplitudeEventOnMount,
  useAnalytics,
} from '../shared/analytics';
import { ChatPage } from './chat';
import { CookiePolicyPage } from './cookie-policy';
import { HomePage } from './home';
import { PagesErrorBoundary } from './pages.error-boundary';
import { PrivacyPolicyPage } from './privacy-policy';
import { TermsOfServicePage } from './terms-of-service';
import styles from './pages.module.scss';

const router = createBrowserRouter([
  {
    ErrorBoundary: PagesErrorBoundary,
    Component: () => (
      <AuthProvider>
        <Outlet />

        <AppCookieBanner />
      </AuthProvider>
    ),
    children: [
      {
        path: '/reset-password/request',
        Component: () => {
          const analytics = useAnalytics();

          return (
            <>
              <LogAmplitudeEventOnMount event='Forgot Password Page Viewed' />
              <ForgotPasswordPage
                onSuccess={() => {
                  analytics.amplitude.track({
                    event: 'User Requested Reset Password',
                  });
                }}
              />
            </>
          );
        },
      },
      {
        path: '/reset-password',
        Component: () => {
          const analytics = useAnalytics();

          return (
            <>
              <LogAmplitudeEventOnMount event='Reset Password Page Viewed' />
              <ResetPasswordPage
                onSuccess={() => {
                  analytics.amplitude.track({ event: 'User Changed Password' });
                }}
              />
            </>
          );
        },
      },
      {
        path: '/privacy-policy',
        Component: () => <PrivacyPolicyPage />,
      },
      {
        path: '/cookie-policy',
        Component: () => <CookiePolicyPage />,
      },
      {
        path: '/terms-of-service',
        Component: () => <TermsOfServicePage />,
      },
      {
        Component: () => (
          <SidebarProvider defaultOpen={!useJWT()}>
            <AuthModal />
            <EmailConfirmationModal />
            <UserProfileDetailsModal />
            <ApplyForAnWhitelistModal />

            <div className={styles.chatApp}>
              <AppSidebar />

              <Outlet />
            </div>
          </SidebarProvider>
        ),
        children: [
          {
            path: '/',
            Component: () => <HomePage />,
          },
          {
            path: '/c/:sessionId',
            Component: () => (
              <JWTGuard>
                <ChatPage />
              </JWTGuard>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to='/' replace />,
      },
    ],
  },
]);

export const Pages = memo(() => <RouterProvider router={router} />);
